import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar, Plus, Edit2, Trash2, Play, Clock, CheckCircle, XCircle, RefreshCw, Info } from "lucide-react";

const InfoTooltip = ({ text }) => (
  <div className="relative inline-block group align-middle ml-1.5 cursor-help">
    <Info size={13} className="text-gray-400 hover:text-[#006080]" />
    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none leading-relaxed">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  </div>
);

const CRON_PRESETS = [
  { label: "Every day at 9am", value: "0 9 * * *" },
  { label: "Every Monday at 9am", value: "0 9 * * 1" },
  { label: "Every hour", value: "0 * * * *" },
  { label: "Every 30 minutes", value: "*/30 * * * *" },
  { label: "First of month", value: "0 9 1 * *" },
  { label: "Custom", value: "custom" },
];

function ScheduleModal({ schedule, uploads, apiDefs, templates, onSave, onClose }) {
  const [form, setForm] = useState({
    name: schedule?.name || "",
    description: schedule?.description || "",
    template_id: schedule?.template_id || "",
    upload_id: schedule?.upload_id || "",
    api_definition_ids: schedule?.api_definition_ids || [],
    cron_expression: schedule?.cron_expression || "0 9 * * 1",
    timezone: schedule?.timezone || "UTC",
    is_active: schedule?.is_active ?? true,
    concurrency: schedule?.concurrency || 1,
    delay_ms: schedule?.delay_ms || 0,
    sync_before_run: schedule?.sync_before_run ?? false,
  });
  const [cronPreset, setCronPreset] = useState("custom");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Detect whether the selected upload is a Google Sheet
  const selectedUpload = uploads.find(u => u._id === form.upload_id);
  const isGoogleSheet = selectedUpload?.source_type === "google_sheets";

  const handleSave = async () => {
    if (!form.name || !form.cron_expression) { setError("Name and cron expression required"); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (e) { setError(formatApiError(e)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{schedule ? "Edit" : "New"} Scheduled Run</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div><Label className="text-xs text-gray-500">Name *</Label><Input className="mt-1" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Daily Sales Processing" /></div>
          <div>
            <Label className="text-xs text-gray-500">Schedule</Label>
            <Select value={cronPreset} onValueChange={v => { setCronPreset(v); if (v !== "custom") set("cron_expression", v); }}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CRON_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="mt-2 font-mono text-sm" value={form.cron_expression} onChange={e => set("cron_expression", e.target.value)} placeholder="0 9 * * 1" />
            <p className="text-xs text-gray-400 mt-1">Cron format: minute hour day month weekday (e.g. <code>0 9 * * 1</code> = every Monday 9am UTC)</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Source File</Label>
              <Select value={form.upload_id || "__none__"} onValueChange={v => set("upload_id", v === "__none__" ? "" : v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select file..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {uploads.map(u => (
                    <SelectItem key={u._id} value={u._id}>
                      {u.source_type === "google_sheets" ? "📊 " : ""}{u.filename}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Template (optional)</Label>
              <Select value={form.template_id || "__none__"} onValueChange={v => set("template_id", v === "__none__" ? "" : v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">None</SelectItem>{templates.map(t => <SelectItem key={t._id} value={t._id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Sync on Schedule — only shown for Google Sheets uploads */}
          {isGoogleSheet && (
            <div className={`rounded-lg border p-3 transition-all ${form.sync_before_run ? "bg-sky-50 border-sky-300" : "bg-gray-50 border-gray-200"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RefreshCw size={14} className={form.sync_before_run ? "text-sky-600" : "text-gray-400"} />
                  <div>
                    <p className={`text-xs font-semibold ${form.sync_before_run ? "text-sky-800" : "text-gray-700"}`}>
                      Sync Google Sheet before run
                    </p>
                    <p className="text-[11px] text-gray-400">
                      Re-fetches the latest rows from the sheet before each execution
                    </p>
                  </div>
                  <InfoTooltip text="When enabled, the scheduler will re-import the linked Google Sheet immediately before firing the execution. This ensures the data is always fresh — even if the sheet was updated since the last run. For private sheets, a service account must be configured in Admin → Google Sheets." />
                </div>
                <Switch
                  checked={form.sync_before_run}
                  onCheckedChange={v => set("sync_before_run", v)}
                  data-testid="sync-before-run-toggle"
                />
              </div>
              {form.sync_before_run && (
                <div className="mt-2 pt-2 border-t border-sky-200">
                  <p className="text-[11px] text-sky-700">
                    Each run will: <strong>1)</strong> Pull latest rows from sheet → <strong>2)</strong> Replace staging data → <strong>3)</strong> Execute API calls
                  </p>
                  {selectedUpload?.source_type === "google_sheets" && !selectedUpload?.sheet_id && (
                    <p className="text-[11px] text-amber-600 mt-1">
                      Warning: Sheet ID not found — the sync may fail. Re-import the sheet from the Upload page.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs text-gray-500">API Definitions to run</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {apiDefs.map(d => {
                const sel = form.api_definition_ids.includes(d._id);
                return <button key={d._id} className={`text-xs px-2 py-1 rounded border transition-colors ${sel ? "bg-[#006080] text-white border-[#006080]" : "bg-white border-gray-200 text-gray-600"}`} onClick={() => set("api_definition_ids", sel ? form.api_definition_ids.filter(x => x !== d._id) : [...form.api_definition_ids, d._id])}>{d.name}</button>;
              })}
            </div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => set("is_active", v)} /><Label className="text-xs text-gray-600">Active (will run on schedule)</Label></div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ScheduledRunsPage() {
  const [schedules, setSchedules] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [apiDefs, setApiDefs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [syncing, setSyncing] = useState({});

  useEffect(() => {
    load();
    client.get("/files/").then(r => setUploads(r.data));
    client.get("/api-defs/").then(r => setApiDefs(r.data));
    client.get("/templates/").then(r => setTemplates(r.data));
  }, []);

  const load = async () => {
    setLoading(true);
    const res = await client.get("/schedules/").catch(() => ({ data: [] }));
    setSchedules(res.data);
    setLoading(false);
  };

  const save = async (form) => {
    if (editingSchedule) await client.put(`/schedules/${editingSchedule._id}`, form);
    else await client.post("/schedules/", form);
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete schedule?")) return;
    await client.delete(`/schedules/${id}`);
    load();
  };

  const trigger = async (id) => {
    await client.post(`/schedules/${id}/trigger`);
    alert("Triggered! Check Executions for results.");
  };

  // Manual Google Sheet sync for a schedule's upload
  const syncNow = async (schedule) => {
    if (!schedule.upload_id) return;
    setSyncing(s => ({ ...s, [schedule._id]: true }));
    try {
      const res = await client.post(`/google-sheets/sync/${schedule.upload_id}`);
      alert(`Synced! ${res.data.rows_imported} rows imported from Google Sheets.`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(s => ({ ...s, [schedule._id]: false }));
    }
  };

  // Lookup upload for a schedule
  const getUpload = (upload_id) => uploads.find(u => u._id === upload_id);

  const statusColor = { success: "#059669", failed: "#DC2626", running: "#006080" };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1"><Calendar size={18} className="text-[#006080]" /><h1 className="text-2xl font-bold text-gray-900">Scheduled Runs</h1></div>
          <p className="text-gray-500 text-sm">Automatically process files on a cron schedule</p>
        </div>
        <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => { setEditingSchedule(null); setShowModal(true); }}><Plus size={15} className="mr-2" /> New Schedule</Button>
      </div>
      {loading ? <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
      : schedules.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <Calendar size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No scheduled runs yet</p>
          <Button className="mt-4 bg-[#006080] text-white" onClick={() => setShowModal(true)}>Create first schedule</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map(s => (
            <div key={s._id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${s.is_active ? "bg-[#e6f2f5]" : "bg-gray-100"}`}>
                  <Calendar size={18} className={s.is_active ? "text-[#006080]" : "text-gray-400"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{s.name}</p>
                    <Badge variant="secondary" className={`text-xs ${s.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>{s.is_active ? "Active" : "Paused"}</Badge>
                    {s.last_run_status && <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ background: `${statusColor[s.last_run_status] || "#9CA3AF"}20`, color: statusColor[s.last_run_status] || "#9CA3AF" }}>{s.last_run_status}</span>}
                    {s.sync_before_run && (
                      <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200">
                        <RefreshCw size={9} /> Syncs Sheet
                        <div className="relative group cursor-help">
                          <Info size={9} className="text-sky-400" />
                          <div className="absolute z-50 bottom-full right-0 mb-1.5 w-52 p-2 bg-gray-900 text-white text-[10px] rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none leading-relaxed">
                            This schedule re-imports the linked Google Sheet before each run to keep data fresh.
                          </div>
                        </div>
                      </span>
                    )}
                  </div>
                  <div className="flex gap-4 mt-1 text-xs text-gray-400 flex-wrap">
                    <span className="font-mono">{s.cron_expression}</span>
                    {s.next_run_at && <span className="flex items-center gap-1"><Clock size={11} /> Next: {new Date(s.next_run_at).toLocaleString()}</span>}
                    {s.last_run_at && <span>Last: {new Date(s.last_run_at).toLocaleString()}</span>}
                    <span>Runs: {s.run_count || 0}</span>
                    {getUpload(s.upload_id)?.source_type === "google_sheets" && (
                      <span className="text-sky-500">📊 {getUpload(s.upload_id)?.filename}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {s.sync_before_run && getUpload(s.upload_id)?.source_type === "google_sheets" && (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-sky-600 border-sky-200 hover:bg-sky-50" onClick={() => syncNow(s)} disabled={syncing[s._id]} title="Re-sync Google Sheet now">
                      <RefreshCw size={11} className={syncing[s._id] ? "animate-spin" : ""} />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs bg-[#006080] text-white hover:bg-[#004d66]" onClick={() => trigger(s._id)}><Play size={11} className="mr-1" /> Run Now</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setEditingSchedule(s); setShowModal(true); }}><Edit2 size={11} /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => del(s._id)}><Trash2 size={11} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showModal && <ScheduleModal schedule={editingSchedule} uploads={uploads} apiDefs={apiDefs} templates={templates} onSave={save} onClose={() => { setShowModal(false); setEditingSchedule(null); }} />}
    </div>
  );
}
